import { Delete, ExpandMore } from '@mui/icons-material';
import {
  Theme,
  darken,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormLabel,
  IconButton
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
import { Link } from '../../../components';

import { IUndefinedPart } from '../../../models';

interface IMobileUndefinedPart {
  original: IUndefinedPart;
  handleDelete?: (val: IUndefinedPart) => void;
}

export const MobileUndefinedPart: FC<IMobileUndefinedPart> = ({ original, handleDelete }) => {
  const classes = useStyles();
  const { description, orderNumbers, createdBy } = original;
  const formatOrderNumbers = () => {
    const compiledOrderNumbers = orderNumbers.map((orderNumber, index) => {
      const seperator = index !== 0 ? ', ' : '';
      return (
        <>
          {seperator}
          <Link
            className={classes.orderNumberLink}
            key={index}
            to={`/production/orders/order-management/${orderNumber}`}
          >
            {orderNumber}
          </Link>
        </>
      );
    });
    return <p>{compiledOrderNumbers}</p>;
  };

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">{description}</Typography>
          <div className={classes.buttonsWrapper}>
            {handleDelete && (
              <IconButton
                className={classes.deleteButton}
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(original);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Order Number(s):
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatOrderNumbers() || '--'}
          </Typography>
        </div>

        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Created By:
          </FormLabel>
          <Typography component="span" variant="body2">
            {createdBy || '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  orderNumberLink: {
    color: darken(theme.palette.primary.main, 0.3),
    '&:hover, &:focus': {
      'text-decoration': 'underline'
    }
  }
}));
